import React from 'react';

interface MatchupEntry {
  id: number;
  name: string;
  score: number;
}

interface Matchup {
  entry1: MatchupEntry;
  entry2?: MatchupEntry;
  winnerId: number;
}

interface Round {
  roundNumber: number;
  matchups: Matchup[];
}

interface Winner {
  playerName: string;
  payout: number;
}

interface BracketResults {
  rounds: Round[];
  winners: Winner[];
}

interface BracketPrintViewProps {
  bracketResults: BracketResults;
  totalBracketPot: number;
  sideEventName: string;
  leagueName?: string;
}

const BracketPrintView: React.FC<BracketPrintViewProps> = ({
  bracketResults,
  totalBracketPot,
  sideEventName,
  leagueName,
}) => {

  const generateBracketHTML = (bracketResults: BracketResults) => {
    // Group rounds by bracket
    const bracketGroups = new Map();
    
    bracketResults.rounds.forEach((round, index) => {
      const bracketNumber = Math.floor(index / (bracketResults.rounds.filter(r => r.roundNumber === 1).length > 1 
        ? bracketResults.rounds.length / bracketResults.rounds.filter(r => r.roundNumber === 1).length 
        : bracketResults.rounds.length)) + 1;
      
      if (!bracketGroups.has(bracketNumber)) {
        bracketGroups.set(bracketNumber, []);
      }
      bracketGroups.get(bracketNumber).push(round);
    });

    // Calculate how many brackets fit per row (2 brackets per page for 8+ person brackets)
    const bracketsPerRow = 2;
    let bracketsHTML = '<div style="display: flex; flex-wrap: wrap; gap: 15px;">';
    
    Array.from(bracketGroups.entries()).forEach(([bracketNumber, rounds], index) => {
      // Organize rounds data
      const roundData = Array.from({ length: rounds.length }, () => []);
      rounds.forEach((round: any) => {
        const roundIndex = round.roundNumber - 1;
        if (roundData[roundIndex]) {
          roundData[roundIndex] = round.matchups;
        }
      });

      const totalRounds = roundData.length;
      const totalFirstRoundMatches = roundData[0]?.length || 0;
      
      // Find the winner and their payout
      let championName = '';
      let championPayout = 0;
      if (roundData[totalRounds - 1] && roundData[totalRounds - 1].length > 0) {
        const finalMatchup: any = roundData[totalRounds - 1][0];
        const winner = finalMatchup.winnerId === finalMatchup.entry1.id ? finalMatchup.entry1 : finalMatchup.entry2;
        championName = winner?.name || '';
        
        const winnerData = bracketResults.winners.find(w => w.playerName === championName);
        championPayout = winnerData?.payout || 0;
      }

      // Add page break after every 2 brackets
      const pageBreak = index > 0 && index % bracketsPerRow === 0 ? 'page-break-before: always;' : '';

      bracketsHTML += `
        <div style="${pageBreak} flex: 1 1 calc(50% - 10px); min-width: 400px; page-break-inside: avoid; border: 2px solid #000; padding: 10px; background: #f9f9f9;">
          <div style="font-size: 14px; font-weight: bold; text-align: center; margin-bottom: 8px; padding: 5px; background: #333; color: white;">
            BRACKET #${bracketNumber}
          </div>
          
          <table style="width: 100%; border-collapse: collapse; font-size: 9px;">
            ${generateBracketTableRows(roundData, totalRounds, totalFirstRoundMatches, championName, championPayout, bracketResults.winners)}
          </table>
        </div>
      `;
    });

    bracketsHTML += '</div>';
    return bracketsHTML;
  };

  const generateBracketTableRows = (
    roundData: any[], 
    totalRounds: number, 
    totalFirstRoundMatches: number,
    championName: string,
    championPayout: number,
    winners: Winner[]
  ) => {
    // Calculate grid dimensions - each matchup takes 2 rows (one per player)
    const totalRows = totalFirstRoundMatches * 2;
    
    // Build rows array
    const rows: string[] = [];
    
    // For each row in the grid
    for (let row = 0; row < totalRows; row++) {
      const cells: string[] = [];
      
      // For each round
      for (let roundIndex = 0; roundIndex < roundData.length; roundIndex++) {
        const roundMatchups = roundData[roundIndex];
        const matchupsInRound = roundMatchups.length;
        const rowsPerMatchup = totalRows / matchupsInRound;
        const matchupIndex = Math.floor(row / rowsPerMatchup);
        const rowInMatchup = row % rowsPerMatchup;
        const matchupCenterRow = Math.floor(rowsPerMatchup / 2);
        const isFinal = roundIndex === totalRounds - 1;
        
        // Only render matchup cells in the center two rows of each matchup's space
        if (rowInMatchup === matchupCenterRow - 1 || rowInMatchup === matchupCenterRow) {
          const matchup = roundMatchups[matchupIndex];
          const isEntry1Winner = matchup.winnerId === matchup.entry1.id;
          const isEntry2Winner = matchup.entry2 && matchup.winnerId === matchup.entry2.id;
          const isPlayer1Row = rowInMatchup === matchupCenterRow - 1;
          
          const playerBgColor = (isPlayer1Row ? isEntry1Winner : isEntry2Winner) 
            ? (isFinal ? '#FFD700' : '#90EE90') 
            : 'white';
          const playerBorder = isFinal ? '2px solid #FFD700' : '1px solid #000';
          const playerName = isPlayer1Row ? matchup.entry1.name : (matchup.entry2?.name || 'BYE');
          const playerScore = isPlayer1Row ? matchup.entry1.score : (matchup.entry2?.score || '-');
          const isWinner = isPlayer1Row ? isEntry1Winner : isEntry2Winner;
          
          cells.push(`
            <td style="border: ${playerBorder}; ${isPlayer1Row ? 'border-bottom: 0.5px solid #ccc;' : 'border-top: 0.5px solid #ccc;'} padding: 3px 5px; background: ${playerBgColor}; font-weight: ${isWinner ? 'bold' : 'normal'}; white-space: nowrap; min-width: 120px;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="flex: 1; overflow: hidden; text-overflow: ellipsis;">${playerName}</span>
                <span style="margin-left: 5px; font-weight: bold; min-width: 22px; text-align: center; background: ${isWinner ? (isFinal ? '#FFD700' : '#d4edda') : '#f5f5f5'}; padding: 2px 4px; border-radius: 2px;">${playerScore}</span>
              </div>
            </td>
          `);
          
          // Add connector cell (only on player 1 row, spans 2 rows)
          if (roundIndex < totalRounds - 1 && isPlayer1Row) {
            cells.push(`<td rowspan="2" style="border-right: 1px solid #000; width: 8px; padding: 0;"></td>`);
          }
        } else {
          // Empty cell for spacing
          cells.push(`<td style="border: 0; padding: 0;"></td>`);
          
          // Connector column - draw vertical lines
          if (roundIndex < totalRounds - 1) {
            const nextMatchupIndex = Math.floor(matchupIndex / 2);
            const nextRowsPerMatchup = rowsPerMatchup * 2;
            const nextMatchupCenterRow = nextMatchupIndex * nextRowsPerMatchup + Math.floor(nextRowsPerMatchup / 2);
            const isTopOfPair = matchupIndex % 2 === 0;
            
            // Draw vertical connector
            if (isTopOfPair) {
              // Top matchup - draw line down
              if (row >= matchupIndex * rowsPerMatchup + matchupCenterRow && row < nextMatchupCenterRow) {
                const drawBottom = row === nextMatchupCenterRow - 1;
                cells.push(`<td style="border-right: 1px solid #000; ${drawBottom ? 'border-bottom: 1px solid #000;' : ''} width: 8px; padding: 0;"></td>`);
              } else {
                cells.push(`<td style="border: 0; width: 8px; padding: 0;"></td>`);
              }
            } else {
              // Bottom matchup - draw line up
              if (row >= nextMatchupCenterRow && row < matchupIndex * rowsPerMatchup + matchupCenterRow - 1) {
                const drawTop = row === nextMatchupCenterRow;
                cells.push(`<td style="border-right: 1px solid #000; ${drawTop ? 'border-top: 1px solid #000;' : ''} width: 8px; padding: 0;"></td>`);
              } else {
                cells.push(`<td style="border: 0; width: 8px; padding: 0;"></td>`);
              }
            }
          }
        }
      }
      
      rows.push(`<tr>${cells.join('')}</tr>`);
    }
    
    // Add champion payout row if needed
    if (championPayout > 0) {
      rows.push(`
        <tr>
          <td colspan="${totalRounds * 2 - 1}" style="background: #228B22; color: white; padding: 4px; text-align: center; font-weight: bold; border: 2px solid #FFD700; margin-top: 5px;">
            🏆 ${championName} - $${championPayout.toFixed(2)}
          </td>
        </tr>
      `);
    }

    return rows.join('');
  };

  const openPrintView = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${leagueName} - ${sideEventName} Tournament Results</title>
        <style>
          @page { margin: 0.5in; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background: white; color: black; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #000; padding-bottom: 15px; }
          .title { font-size: 26px; font-weight: bold; margin-bottom: 10px; }
          .subtitle { font-size: 18px; margin-bottom: 10px; font-weight: bold; color: #333; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; border-top: 1px solid #000; padding-top: 10px; }
          @media print { .bracket-container { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">${leagueName}</div>
          <div class="subtitle">${sideEventName} - Tournament Brackets</div>
          <div style="font-size: 16px; color: #666;">Total Prize Pool: $${Number(totalBracketPot || 0).toFixed(2)}</div>
        </div>
        ${generateBracketHTML(bracketResults)}
        <div class="footer">
          Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
        </div>
      </body>
      </html>
    `;

    // Open in new window for printing
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  return (
    <button
      onClick={openPrintView}
      className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
      </svg>
      Print Brackets
    </button>
  );
};

export default BracketPrintView;